import { type Props } from "react-rnd";
import {
  useLayoutEffect,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import useDefaultSize from "./useDefaultSize";
import useMinMaxRef from "./useMinMaxRef";
import { minMaxSize, parseSnap } from "../functions";
import { useWindows } from "../../../contexts/windows";
import { useWindow } from "@/contexts/windows/window";
import { useSession } from "../../../contexts/session";
import { useSignal } from "@/contexts/signal";
import { useMainElements } from "@/contexts/elements";
import { useConfig } from "@/contexts/config";
import { getElementSize, pxToNum } from "@/utils/functions";
import { SnapInfo, SnapType } from "@/contexts/windows/types";
import {
  TRANSITIONS_IN_MILLISECONDS,
  TRANSITIONS_IN_SECONDS,
} from "@/utils/constants";
export type Size = NonNullable<Props["size"]>;

type Resizable = [Size, React.Dispatch<React.SetStateAction<Size>>];

// TODO: When resizing, look for any other snapped windows which is not the current window and not minimized or not maximized, and has a snap value,
// TODO: Detect the resize size, take the percentage out of it, update current window's percent, and maybe do 100 - currentPercent = the other ones to work with.
// TODO: Really have to think this whole thing through tho ^;
const useResizable = (id: string, autoSizing = false): Resizable => {
  const defaultSize = useDefaultSize(id);
  const { events: RD_Events } = useSignal();
  const {
    windowStates: {
      [id]: { size: stateSize = defaultSize } = {},
      ...otherWindowStates
    } = {},
  } = useSession();

  const { disableAnimations } = useConfig();
  const {
    windows: {
      [id]: {
        lockAspectRatio = false,
        snap = false as SnapType,
        closing = false,
        maximized = false,
      } = {},
      ...otherWindows
    },
    setSnap,
  } = useWindows();
  const { Events: RD_WindowEvents } = useWindow();
  const { WindowsViewArea, WindowsViewAreaSize } = useMainElements();

  const [size, setSize] = useState<Size>(() =>
    minMaxSize(stateSize, lockAspectRatio)
  );
  const blockAutoSizeRef = useMinMaxRef(id);
  const isSnap =
    !closing && snap && typeof snap === "object" && typeof snap !== "boolean";

  const onResize = useCallback(
    (newSize: Size) => {
      if (isSnap) {
        const currentWindowsViewSize = WindowsViewArea
          ? getElementSize(WindowsViewArea)
          : WindowsViewAreaSize;
        const newWidthPercent =
          pxToNum(newSize.width) / pxToNum(currentWindowsViewSize.width);
        const newHeightPercent =
          pxToNum(newSize.height) / pxToNum(currentWindowsViewSize.height);

        const newHeightPercentNice = newHeightPercent * 100;
        const newWidthPercentNice = newWidthPercent * 100;
        /*
        console.log("Size changed + it is snap :: ", id, {
          newSize,
          newWidthPercent,
          newHeightPercent,
        });
        */
        const availableWidthPercent = 100 - newWidthPercentNice;
        let availableHeightPercent = 100;
        availableHeightPercent -= newHeightPercentNice;
        const relatedWindows = Object.entries(otherWindows).filter(
          ([id, { snap: someWindowSnap, closing }]) => {
            return (
              !closing &&
              someWindowSnap &&
              typeof someWindowSnap !== "boolean" &&
              typeof snap !== "boolean" &&
              someWindowSnap.type === snap?.type &&
              someWindowSnap.index !== snap?.index
            );
          }
        );

        setSnap(id, {
          ...(snap as SnapInfo),
          widthPercent: newWidthPercentNice,
          heightPercent: newHeightPercentNice,
        } as SnapInfo);

        relatedWindows.forEach(([someWindowID, { snap: someWindowSnap }]) => {
          /*
          console.log({
            someWindowID,
            availableWidthPercent,
            availableHeightPercent,
          });
          */
          const newSnap: SnapInfo = {
            ...(someWindowSnap as SnapInfo),
          } as SnapInfo;

          if (snap.type === "twoColumns") {
            newSnap.widthPercent = availableWidthPercent;
          } else if (snap.type === "threeColumns") {
            newSnap.widthPercent = Math.abs(
              availableWidthPercent - newSnap.widthPercent
            );
          }
          setSnap(someWindowID, {
            ...(someWindowSnap as SnapInfo),
            ...newSnap,
          });
          RD_Events.emit(
            "event:elements/resize/windowsview",
            currentWindowsViewSize
          );
        });
      }
    },
    [
      isSnap,
      WindowsViewArea,
      WindowsViewAreaSize,
      id,
      RD_Events,
      otherWindows,
      setSnap,
      snap,
    ]
  );

  useLayoutEffect(() => {
    const registeredEvents = [
      RD_WindowEvents.on(
        `event:window/resize`,
        ({ size: newSize, ...other }) => {
          if (other.user_resize) {
            onResize(newSize);
          }
        }
      ),
    ];
    return () => {
      registeredEvents.forEach(RD_Events.removeListenerByID);
    };
  }, [onResize, RD_Events, id]);
  const announceNewSize = useCallback(
    ({
      size,
      maximized = false,
      user_resize = false,
    }: {
      size: Size;
      maximized?: boolean;
      user_resize?: boolean;
    }) => {
      // console.log("NEW SIZE ANNOUNCED");
      const eventMsg = {
        maximized,
        size: size,
        user_resize
      };
      RD_WindowEvents.emit("event:window/resize", eventMsg);
      RD_Events.emit(
        ["event:windows/resize", `event:windows/resize:${id}`],
        eventMsg
      );
    },
    [RD_WindowEvents, RD_Events]
  );
  useLayoutEffect(() => {
    if (maximized) {
      if (disableAnimations) {
        window.requestAnimationFrame(() =>
          announceNewSize({
            size: getElementSize(WindowsViewArea),
            maximized: true,
          })
        );
      } else {
        setTimeout(() => {
          window.requestAnimationFrame(() =>
            announceNewSize({
              size: getElementSize(WindowsViewArea),
              maximized: true,
            })
          );
        }, TRANSITIONS_IN_MILLISECONDS.WINDOW + 150);
      }
    }

    const registeredEvents = [];
    const monitorViewportResize = (newWindowsViewSize: Size): void => {
      if (maximized) {
        if (disableAnimations) {
          window.requestAnimationFrame(() =>
            announceNewSize({ size: newWindowsViewSize, maximized: true })
          );
        } else {
          setTimeout(() => {
            window.requestAnimationFrame(() =>
              announceNewSize({
                size: newWindowsViewSize,
                maximized: true,
              })
            );
          }, TRANSITIONS_IN_MILLISECONDS.WINDOW + 150);
        }
      }
    };
    if (maximized) {
      registeredEvents.push(
        RD_Events.on("event:elements/resize/windowsview", monitorViewportResize)
      );
    }
    return () => {
      // Cleanup
      registeredEvents.forEach(RD_Events.removeListenerByID);
    };
  }, [id, disableAnimations, maximized, WindowsViewArea, announceNewSize]);

  useLayoutEffect(() => {
    if (!maximized) {
      announceNewSize({ size, user_resize: false, maximized: false });
    }
  }, [maximized, size, id, announceNewSize]);

  useLayoutEffect(() => {
    if (autoSizing && !blockAutoSizeRef.current) {
      setSize(minMaxSize(stateSize, lockAspectRatio));
    }
  }, [autoSizing, blockAutoSizeRef, lockAspectRatio, stateSize]);

  return [size, setSize];
};

export default useResizable;
